import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {change, Field, reduxForm} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {SelectInput} from 'admin-on-rest';
import {convertDate} from '../shared/functions';
import {TIME_ZONE_OFFSETS} from '../shared/dateTimeLists';
import {filterStyles} from '../shared/styles-filter';
import FormattedDateTextInput from './FormattedDateTextInput';
import RaisedButton from 'material-ui/RaisedButton';
import FuseTheme from '../global/theme';

const styles = {
    buttonContainer: {display: 'inline-block', margin: '21px 0 0 0', verticalAlign: 'bottom'},
    button: {color: FuseTheme.palette.primary3Color, verticalAlign: 'bottom'},
    iconButton: {color: FuseTheme.palette.primary3Color},
    submitButton: {display: 'block', float: 'right', margin: '1em 0 1em 0'}
};

/**
 * A form containing a SelectInput component with Mode choices
 *
 */
class DateRangeFilterWithSubmit extends Component {
    constructor(props) {
        super(props);
        this.state = {
            displayDateFilter: false,
            selectedStartDate: null,
            selectedStartDateObject: null,
            selectedEndDate: null,
            selectedOffset: (props.includeOffset && props.selectedOffset) ? props.selectedOffset : '-07:00'
        };
        this.setEndDateLimit = this.setEndDateLimit.bind(this);
        this.handleStartDateSelect = this.handleStartDateSelect.bind(this);
        this.handleEndDateSelect = this.handleEndDateSelect.bind(this);
        this.handleOffsetSelect = this.handleOffsetSelect.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    setEndDateLimit() {
        const {selectedStartDateObject} = this.state;

        const {endMaxDate, limitEndRange} = this.props;

        if (endMaxDate) {
            // If an End date is set, use it
            return endMaxDate;
        }
        if (limitEndRange) {
            let today = new Date();
            if (limitEndRange === '1-year-after' && selectedStartDateObject) {
                const dateObj = new Date(selectedStartDateObject);
                // The End date may be no more than a year after the selected Start date
                let endDate = new Date(dateObj.getTime());
                endDate.setMonth(endDate.getMonth() + 12);
                if (endDate.getTime() <= today.getTime()) {
                    return endDate;
                }
            }
        }
        // Otherwise, the End date can be no later than today
        return new Date();
    }

    /* Selection Processing ------------------------------ */

    /**
     * Handle selection event for Start Date
     *
     * @param event
     * @param index
     * @param value
     */
    handleStartDateSelect(event, index, value) {
        if (index && index !== value) {
            // Convert date selection from YYYY-MM-DDT00:00:00.000Z to yyyy-mm-dd
            const dateStr = convertDate(index, 'query', false, true);
            const dateYear = new Date(index).getFullYear();
            if (dateYear && dateYear > 2015) {
                this.setState({
                    selectedStartDate: dateStr,
                    selectedStartDateObject: index
                }, () => {
                    this.props.dispatch(change('filterForm', 'start_date', dateStr));
                    if (typeof this.props.onSelectStartDate === 'function') {
                        this.props.onSelectStartDate('start_date', dateStr);
                    }
                });
            }
        }
    }

    /**
     * Handle selection event for End Date
     *
     * @param event
     * @param index
     * @param value
     */
    handleEndDateSelect(event, index, value) {
        if (index && index !== value) {
            // Convert date selection from YYYY-MM-DDT00:00:00.000Z to yyyy-mm-dd
            const dateStr = convertDate(index, 'query', false, true);
            const dateYear = new Date(index).getFullYear();
            if (dateYear && dateYear > 2015) {
                this.setState({
                    selectedEndDate: dateStr,
                }, () => {
                    this.props.dispatch(change('filterForm', 'end_date', dateStr));
                    if (typeof this.props.onSelectEndDate === 'function') {
                        this.props.onSelectEndDate('end_date', dateStr);
                    }
                });
            }
        }
    }

    /**
     * Handle selection event for Time Zone Offset
     *
     * @param event
     * @param index
     * @param value
     */
    handleOffsetSelect(event, index, value) {
        if (index && index !== value) {
            this.setState({
                selectedOffset: index,
            }, () => {
                this.props.dispatch(change('filterForm', 'offset', index));
                if (typeof this.props.onSelectOffset === 'function') {
                    this.props.onSelectOffset('offset', index);
                }
            });
        }
    }

    handleSubmit() {
        if (typeof this.props.onSubmit === 'function') {
            this.props.onSubmit(this.state.selectedStartDate, this.state.selectedEndDate, this.state.selectedOffset);
        }
    }

    render() {
        const {
            dataState,
            includeOffset,
            startMaxDate,
            startMinDate
        } = this.props;

        const {
            selectedStartDateObject,
            selectedStartDate,
            selectedEndDate,
            selectedOffset
        } = this.state;

        const today = new Date();
        const endMaxDateObject = this.setEndDateLimit();

        // If the Start date is more than a year back, the End date should display year selection first
        const displayEndYearFirst = endMaxDateObject && endMaxDateObject.getTime() < today.getTime();
        const endValue = displayEndYearFirst ? null : endMaxDateObject;

        return (<form>
            <div className="list-filter" style={filterStyles.filterContainerLeft}>
                <FormattedDateTextInput
                    name="start_date"
                    source="start_date"
                    label="Select a Start Date:"
                    onChangeFunc={this.handleStartDateSelect}
                    minDate={startMinDate ? startMinDate : null}
                    maxDate={startMaxDate ? startMaxDate : new Date()}
                    style={filterStyles.inline}
                />

                <FormattedDateTextInput
                    name="end_date"
                    source="end_date"
                    label="Select an End Date:"
                    onChangeFunc={this.handleEndDateSelect}
                    minDate={selectedStartDateObject}
                    maxDate={endMaxDateObject}
                    value={endValue}
                    isDisabled={!selectedStartDateObject}
                    style={filterStyles.inline}
                />
            </div>
            <div className="list-filter" style={filterStyles.filterContainerLeft}>
                {includeOffset &&
                    <Field
                        name="offset"
                        component={SelectInput}
                        label="Select a Time Zone Offset"
                        choices={TIME_ZONE_OFFSETS}
                        onChange={this.handleOffsetSelect}
                        elStyle={{...filterStyles.inline, ...filterStyles.selectInput}}
                        options={{
                            value: this.props.selectedOffset
                        }}
                    />
                }

            </div>
            <div className="list-filter" style={filterStyles.filterContainerLeft}>
                <RaisedButton
                    primary
                    type="button"
                    label="Search"
                    style={styles.buttonContainer}
                    buttonStyle={styles.button}
                    disabled={(!selectedStartDate || !selectedEndDate || !selectedOffset || dataState === 'fetching')}
                    onClick={() => this.handleSubmit()}
                />
            </div>
            <hr style={filterStyles.break}/>
        </form>);
    }
}

const mapStateToProps = (state, ownProps) => {
    let filterForm = state.form['filterForm'];
    let timeZoneForm = state.form['timeZoneForm'];
    const defaultOffsetValue = (timeZoneForm && timeZoneForm.values && timeZoneForm.values['timeZoneDefault']) ? timeZoneForm.values['timeZoneDefault'] : null;
    if (filterForm && filterForm.values) {
        const startDateValue = filterForm.values['start_date'] ? filterForm.values['start_date'].toString() : null;
        const endDateValue = filterForm.values['end_date'] ? filterForm.values['end_date'].toString() : null;
        const offsetValue = filterForm.values['offset'] ? filterForm.values['offset'].toString() : defaultOffsetValue;
        return {
            selectedStartDate: startDateValue,
            selectedEndDate: endDateValue,
            selectedOffset: offsetValue
        };
    }
    return {
        selectedOffset: defaultOffsetValue
    };
};

DateRangeFilterWithSubmit.propTypes = {
    basePath: PropTypes.string,
    dataState: PropTypes.string,
    endMaxDate: PropTypes.object,
    includeOffset: PropTypes.bool,
    limitEndRange: PropTypes.string,
    onSelectEndDate: PropTypes.func,
    onSelectStartDate: PropTypes.func,
    onSelectOffset: PropTypes.func,
    onSubmit: PropTypes.func,
    startMaxDate: PropTypes.object,
    startMinDate: PropTypes.object
};

DateRangeFilterWithSubmit.defaultProps = {
    dataState: 'waiting',
    endMaxDate: null,
    includeOffset: true,
    startMaxDate: null,
    startMinDate: null
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'filterForm',
        enableReinitialize: true,
    })
);

export default enhance(DateRangeFilterWithSubmit);



// WEBPACK FOOTER //
// ./src/Fuse/components/DateRangeFilterWithSubmit.js